import { Alert, SpaceBetween } from "@amzn/awsui-components-react-v3";
import { useEffect, useRef } from "react";
import { Alerts as AlertsType } from "../lib/types";

interface AlertsProps {
  items: AlertsType;
  onDismiss?: (key: string) => void;
}

const DEFAULT_AUTO_DISMISS_SECONDS = 10;

export function Alerts({ items, onDismiss }: AlertsProps) {
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    if (!onDismiss) return;
    Object.entries(items).forEach(([key, alert]) => {
      if (alert.autoDismiss && !timersRef.current[key]) {
        const duration =
          (alert.autoDismissSeconds ?? DEFAULT_AUTO_DISMISS_SECONDS) * 1000;
        timersRef.current[key] = setTimeout(() => {
          delete timersRef.current[key];
          onDismiss(key);
        }, duration);
      }
    });
    // Clean up timers for keys that no longer exist
    Object.keys(timersRef.current).forEach((key) => {
      if (!items[key]) {
        clearTimeout(timersRef.current[key]);
        delete timersRef.current[key];
      }
    });
  }, [items, onDismiss]);

  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(clearTimeout);
      timersRef.current = {};
    };
  }, []);

  return (
    <SpaceBetween direction="vertical" size="xs">
      {Object.keys(items).map((key) => (
        <Alert
          key={key}
          type={items[key].type}
          data-testid={`alert-${key}`}
          dismissible={!!onDismiss}
          onDismiss={onDismiss ? () => onDismiss(key) : undefined}
        >
          {items[key].message}
        </Alert>
      ))}
    </SpaceBetween>
  );
}
